<template>
  <div>
    <static-alert v-if="message" :message="message" @on-hide="message=''" />

    <div>
      <div v-if="range?.preset === 'last7days'" class="mb-5">
        <div class="mb-3">
          <strong>Recent Sales</strong>
        </div>
        <orders-recent />
      </div>

      <div v-else class="small d-flex align-items-center justify-content-end mb-3">
        <span>View recent sales →</span>
        <button type="button" class="btn btn-link btn-sm" @click="emits('reset-date')">Reset to Last 7 Days</button>
      </div>
    </div>


    <div class="row small fw-bold summary-cards mb-3">
      <div :class="showDetailedView ? 'col-4' : 'col-6'" class="mb-2 mb-md-0" style="cursor:pointer" @click="$router.push({'name': 'InsightsSales'})">
        <div class="bg-info-subtle rounded-pill p-1  text-center">
          <money-block :dollar="summary.sales" class="display" />
          SALES
        </div>
      </div>
      <div v-if="showDetailedView" class="col-4 mb-2 mb-md-0" style="cursor:pointer" @click="$router.push({'name': 'InsightsSales'})">
        <div class="bg-danger-subtle rounded-pill p-1 text-center">
            <money-block :dollar="summary.shipping + summary.fees + summary.marketing + summary.refunds + summary.cogs"  class="display-small" />
          FEES+COSTS
        </div>
      </div>
      <div :class="showDetailedView ? 'col-4' : 'col-6'" class="mb-2 mb-md-0" style="cursor:pointer" @click="$router.push({'name': 'InsightsSales'})">
        <div class="bg-success-subtle rounded-pill p-1  text-center">
          <money-block :dollar="summary.net_sales"  class="display" />
          PROFIT
        </div>
      </div>
    </div>

    <chart-wrapper v-if="salesSeries" :key="showDetailedView ? 'detailed' : 'simple'" :chart-options="salesOptions" :chart-series="salesSeries" class="mb-4" />

    <div class="row small fw-bold summary-cards mb-5">
      <div class="mb-2 mb-md-0" :class="crosslisting ? 'col-md-4' : 'col-md-6'" style="cursor:pointer" @click="$router.push({'name': 'InsightsSpeed'})">
        <div class="border rounded-pill p-1 text-center">
          ITEMS SOLD
          <div>
            <div v-if="chart2Data && chart2Data.platform_sales && chart2Data.platform_sales.length > 0" class="display text-info">{{ itemCount }}</div>
            <div v-else class="display text-info">—</div>
          </div>
        </div>
      </div>
      <div class="mb-2 mb-md-0" :class="crosslisting ? 'col-md-4' : 'col-md-6'" style="cursor:pointer" @click="$router.push({'name': 'InsightsSales'})">
        <div class="border rounded-pill p-1 text-center">
          AVG PROFIT / ITEM
          <div>
            <money-block v-if="chart2Data && chart2Data.platform_sales && chart2Data.platform_sales.length > 0" :dollar="avgItemNet" class="display" />
            <div v-else class="display text-info">—</div>
          </div>
        </div>
      </div>
      <cross-listing-gate>
      <div class="col-md-4 mb-2 mb-md-0" style="cursor:pointer" @click="$router.push({'name': 'InsightsSales'})">
        <div class="border rounded-pill p-1 text-center">
          TOP PLATFORM
          <div class="d-flex align-items-center justify-content-center">
            <div v-if="chart2Data && chart2Data.platform_sales && chart2Data.platform_sales.length > 0" >
              <div class="display text-info">{{ chart2Data.platform_sales[0].platform }}</div>
              <money-block :dollar="chart2Data.platform_sales[0].net_sales" class="ms-2" />
            </div>
            <div v-else class="display text-info">—</div>
          </div>

        </div>
      </div>
      </cross-listing-gate>
    </div>

    <div class="row small fw-bold summary-cards mb-5">

      <div class="col-md-4 mb-2 mb-md-0 position-relative" style="cursor:pointer"
           @click="$router.push({'name': 'InsightsSpeed'})"
           @mouseenter="fastestHover=true" @mouseleave="fastestHover=false">
        <div class="border rounded-pill p-1 text-center">
          <i class="bi bi-lightning me-1 text-warning"></i>
          FASTEST FLIP
          <div class="d-flex align-items-center justify-content-center">
            <div class="display text-info">
              <span v-if="fastestItem">{{ fastestFlipLabel }}</span>
              <span v-else>—</span>
            </div>
          </div>
        </div>
        <div v-if="fastestHover && fastestItem" class="oldest-hover-card">
          <img v-if="fastestItem.thumbnail" :src="fastestItem.thumbnail" class="oldest-thumb" />
          <div class="oldest-title">{{ fastestItem.title }}</div>
        </div>
      </div>

      <div class="col-md-4 mb-2 mb-md-0 position-relative" style="cursor:pointer"
           @click="$router.push({'name': 'InsightsHealth'})"
           @mouseenter="oldestHover=true" @mouseleave="oldestHover=false">
        <div class="border rounded-pill p-1 text-center">
          <i class="bi bi-hourglass-split text-info me-1"></i> OLDEST LISTING
          <div class="d-flex align-items-center justify-content-center">
            <div class="display text-info">
              <span v-if="oldestListing">{{ oldestListingLabel }}</span>
              <span v-else>—</span>
            </div>
          </div>
        </div>
        <div v-if="oldestHover && oldestListing" class="oldest-hover-card">
          <img v-if="oldestListing.thumbnail" :src="oldestListing.thumbnail" class="oldest-thumb" />
          <div class="oldest-title">{{ oldestListing.title }}</div>
        </div>
      </div>

      <div class="col-md-4 mb-2 mb-md-0" style="cursor:pointer" @click="$router.push({'name': 'InsightsHealthListing'})">
        <div class="border rounded-pill p-1 text-center">
          <i class="bi bi-percent text-success me-1"></i> LISTING CONVERSION
          <div class="d-flex align-items-center justify-content-center">
            <div class="display text-info">
              <span v-if="listingConversion !== null">{{ listingConversion }}%</span>
              <span v-else>—</span>
            </div>
          </div>
          <div v-if="listingConversion !== null" class="text-muted" style="font-size:0.65rem;">{{ itemCount }} sold / {{ newListingsCount }} listed</div>
        </div>
      </div>

    </div>

    <cogs-issues-panel />

  </div>
</template>

<script setup>
import { computed, nextTick, ref, defineProps, watch, onMounted, defineEmits } from 'vue'
import { useStore } from 'vuex'
import StaticAlert from "@/app/components/Utils/StaticAlert.vue";
import ChartWrapper from "@/app/components/Utils/ChartWrapper.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import { DateUtil, flipTimeBreakdown } from "@/services/dateUtils";
import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";
import OrdersRecent from "@/app/components/orders/OrdersRecent.vue";
import CogsIssuesPanel from "@/app/components/inventory/CogsIssuesPanel.vue";

const props = defineProps({
  range: { type: Object, required: true },
})

const emits = defineEmits(['reset-date'])

const store = useStore()
const message = ref('')
const orderData = ref([])
const showDetailedView = ref(false)
const chart2Data = ref()
const fastestItem = ref(null)
const fastestHover = ref(false)
const oldestListing = ref(null)
const oldestHover = ref(false)
const newListingsCount = ref(null)

const storeConfig = computed(() => store.state.eshop.eshop)
const dateUtil = computed(() => new DateUtil(storeConfig.value?.timezone || store.state.defaultTimezone))
const crosslisting = computed(() => store.state.eshop.eshop?.config?.CROSSLISTING)

const comparisonMode = computed(() => props.range?.comparison || 'prev')
const comparisonOrderData = ref([])
const COMP_LABELS = { prev: 'previous period', yoy: 'year over year' }

const comparisonChartData = computed(() =>
  comparisonOrderData.value.map(key => ({
    net_sales: parseFloat(key.net || 0),
  }))
)

function getComparisonDates() {
  const from = props.range.from
  const to = props.range.to
  if (comparisonMode.value === 'yoy') {
    return {
      from: from?.minus ? from.minus({ years: 1 }) : new Date(new Date(from).setFullYear(new Date(from).getFullYear() - 1)),
      to: to?.minus ? to.minus({ years: 1 }) : new Date(new Date(to).setFullYear(new Date(to).getFullYear() - 1))
    }
  }
  // 'prev' — shift back by range span for non-overlapping periods
  const spanDays = from?.diff
    ? Math.floor(to.startOf('day').diff(from.startOf('day'), 'days').days) + 1
    : Math.floor((new Date(to).setHours(0,0,0,0) - new Date(from).setHours(0,0,0,0)) / 86400000) + 1
  return {
    from: from?.minus ? from.minus({ days: spanDays }) : new Date(new Date(from).getTime() - spanDays * 86400000),
    to: to?.minus ? to.minus({ days: spanDays }) : new Date(new Date(to).getTime() - spanDays * 86400000)
  }
}

async function loadComparisonOrders() {
  const { from, to } = getComparisonDates()
  const fromDate = dateUtil.value.toISODate(from)
  const toDate = dateUtil.value.toISODate(to)
  let interval = props.range.interval
  if (interval === 'quarterWeek') interval = 'week'
  const response = await store.state.api.get('/orders/aggregate-orders-by-range', {
    params: { fdate: fromDate, tdate: toDate, interval }
  })
  comparisonOrderData.value = response.data.orders
}

const summary = computed(() => {
  return orderData.value.reduce((agg, cur) => {
    agg.net_sales += parseFloat(cur.net || 0)
    agg.income    += (parseFloat(cur.gross || 0) + parseFloat(cur.credits || 0))
    agg.sales     += (parseFloat(cur.sales || 0)  + parseFloat(cur.credits || 0))
    agg.shipping  += parseFloat(cur.shipping || 0)
    agg.refunds   += parseFloat(cur.refunds || 0)
    agg.fees      += parseFloat(cur.fees || 0)
    agg.marketing += parseFloat(cur.marketing || 0)
    agg.disputes  += parseFloat(cur.disputes || 0)
    agg.cogs      += parseFloat(cur.cogs || 0)
    agg.count     += parseInt(cur.order_count || 0)
    return agg
  }, {net_sales:0, income:0, sales:0, shipping:0, refunds:0, fees:0, marketing:0, disputes:0, cogs:0, count:0})
})

const chartData = computed(() => {
  return orderData.value.map(key => ({
    sale_date: key.agg_date,
    net_sales: parseFloat(key.net || 0),
    income: parseFloat(key.gross || 0) + parseFloat(key.credits || 0),
    shipping: parseFloat(key.shipping || 0),
    expenses: parseFloat(key.fees || 0) + parseFloat(key.marketing || 0),
    refunds: parseFloat(key.refunds || 0),
  }))
})

const salesSeries = computed(() => {
  const allSeries = [
    {
      name: 'expenses',
      type: 'bar',
      data: chartData.value.map(c => c.expenses.toFixed(2)),
    },
    {
      name: 'income',
      type: 'bar',
      data: chartData.value.map(c => c.income.toFixed(2))
    },
    {
      name: 'net',
      type: 'line',
      data: chartData.value.map(c => c.net_sales.toFixed(2)),
    },
    {
      name: 'refunds',
      type: 'line',
      data: chartData.value.map(c => c.refunds.toFixed(2))
    }
  ]

  const activeSeries = showDetailedView.value
    ? allSeries
    : allSeries.filter(s => s.name === 'income' || s.name === 'net')

  if (comparisonMode.value && comparisonChartData.value.length > 0) {
    activeSeries.push({
      name: `net (${COMP_LABELS[comparisonMode.value]})`,
      type: 'line',
      data: comparisonChartData.value.map(c => c.net_sales.toFixed(2)),
    })
  }

  return activeSeries
})

const salesOptions = computed(() => {
  const lineWidth = chartData.value.length > 12 ? 3 : 6
  const hasComp = comparisonMode.value && comparisonChartData.value.length > 0

  const strokeWidths = showDetailedView.value
    ? [0, 0, lineWidth, 3]
    : [0, lineWidth]

  const dashArray = showDetailedView.value
    ? [0, 0, 0, 0]
    : [0, 0]

  const colors = showDetailedView.value
    ? ["rgb(247, 221, 220)", 'rgb(211, 232, 211)', "forestgreen", "rgb(217, 83, 79)"]
    : ['rgb(211, 232, 211)', "forestgreen"]

  if (hasComp) {
    strokeWidths.push(chartData.value.length > 12 ? 2 : 3)
    dashArray.push(6)
    colors.push('rgba(139, 202, 250, 0.5)')
  }

  const enabledSeries = showDetailedView.value
    ? [2, 3]
    : [1]

  return {
    title: '',
    chart: { height: showDetailedView.value ? 500 : 300 },
    labels: chartData.value.map(c => c.sale_date),
    stroke: {
      width: strokeWidths,
      dashArray,
    },
    dataLabel: {
      enabled: chartData.value.length < 15,
      enabledOnSeries: enabledSeries,
    },
    colors,
  }
})

//const itemCount = computed(() => orderData.value.reduce((agg, o) => agg + o.order_count, 0))

const itemCount = computed(() => chart2Data.value && chart2Data.value.item_count || 0)
const ABBR = { min: 'm', hrs: 'h', days: 'd' }
const fastestFlipLabel = computed(() => {
  if (!fastestItem.value) return null
  const { value, unit } = flipTimeBreakdown(fastestItem.value.minutes_to_sell)
  return `${value}${ABBR[unit]}`
})
const oldestListingLabel = computed(() => {
  if (!oldestListing.value) return null
  const days = oldestListing.value.listing_age_days
  return days >= 90 ? `${Math.floor(days / 30)}mo` : `${days}d`
})

const avgItemNet = computed(() => {
  if (!itemCount.value) return 0;
  return chart2Data.value.total_net/itemCount.value;
})

const listingConversion = computed(() => {
  if (!newListingsCount.value || !itemCount.value) return null
  return ((itemCount.value / newListingsCount.value) * 100).toFixed(1)
})

async function onDateChange() {
  await loadOrdersByRange()
  await loadSales()
  fetchFastestItem()
  fetchNewListingsCount()
  loadComparisonOrders()
}

function fetchNewListingsCount() {
  const fromDate = props.range.from
  const toDate = props.range.to
  store.state.api.get('/inventory/new-listings-count', { params: { from: fromDate, to: toDate } })
    .then(res => { newListingsCount.value = res.data?.count ?? null })
    .catch(() => { newListingsCount.value = null })
}

function fetchOldestListing() {
  store.state.api.get('/inventory/oldest-active-listing')
    .then(res => { oldestListing.value = res.data?.item || null })
    .catch(() => { oldestListing.value = null })
}

function fetchFastestItem() {
  const fromDate = props.range.from
  const toDate = props.range.to
  store.state.api.get('/inventory/fastest-sale', { params: { from: fromDate, to: toDate } })
    .then(res => { fastestItem.value = res.data?.item || null })
    .catch(() => { fastestItem.value = null })
}

async function loadSales () {
  await nextTick()
  fetchChartData()
}

async function loadOrdersByRange() {
  const fromDate = dateUtil.value.toISODate(props.range.from)
  const toDate = dateUtil.value.toISODate(props.range.to)
  let interval = props.range.interval
  if (interval === 'quarterWeek') interval = 'week'
  await nextTick()
  const orders = await store.state.api.get('/orders/aggregate-orders-by-range', {params: {
    fdate: fromDate,
    tdate: toDate,
    interval: interval
  }})

  orderData.value = orders.data.orders
}

function fetchChartData() {
  const fromDate = props.range.from //.toISOString().split('T')[0]
  const toDate = props.range.to //.toISOString().split('T')[0]

  store.dispatch("eshop/getOrdersChartData", { fromDate, toDate })
      .then(data => {
        chart2Data.value = data
      })
      .catch(error => {
        console.error('Failed to load chart data:', error)
      })
}

onMounted(async () => {
  await nextTick()
  await onDateChange()
  fetchOldestListing()
})

watch(() => props.range, async () => {
  await onDateChange()
}, { deep: true })

</script>

<style lang="scss" scoped>
  .chart {min-height: 50rem;}

  .oldest-hover-card {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 8px;
    width: 160px;
    z-index: 100;
    text-align: center;
    pointer-events: none;
  }

  .oldest-thumb {
    width: 100%;
    height: 100px;
    object-fit: contain;
    border-radius: 4px;
    margin-bottom: 6px;
  }

  .oldest-title {
    font-size: 0.7rem;
    font-weight: normal;
    color: #333;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>


